// src/pages/memberProfile/PartnerLifestyleInfo.jsx

import { useEffect, useState, useCallback } from "react";
import { useOutletContext, useParams, useNavigate } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import cls from "./PartnerLifestyleInfo.module.scss";
import api from "@/utils/axios";
import i18n from "@/i18n";
import { toast } from "react-toastify";

import {
  SEXUAL_IDENTITIES,
  SEXUAL_ROLES,
  ATTRACTION_GENDERS,
  CONNECTION_TYPES,
  INTIMACY_TYPES,
  SEXUAL_FANTASIES,
  BODY_TYPES,
  BODY_ATTRACT,
  SEXUAL_ENVIRONMENTS,
  EVENT_ATTRACT,
  ALCOHOL_CANNABIS,
  BE_MATCHED,
  JOIN_HOST,
} from "@/enums/lifestyleEnums";

const fadeIn = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.25, ease: "easeOut" } },
};

const pageStagger = {
  show: {
    transition: { staggerChildren: 0.05 },
  },
};

const initialFormState = {
  sexual_identity: "",
  sexual_role: "",
  attraction_genders: [],
  connection_types: [],
  intimacy_types: [],
  sexual_fantasies: [],
  body_type: "",
  body_attract: [],
  sexual_environment: [],
  event_attract: [],
  alcohol_cannabis: "",
  be_matched: [],
  join_host: "",
};

function fallbackLabelFromKey(key) {
  if (!key) return "";
  return key
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

const PartnerLifestyleInfo = () => {
  const outletCtx = useOutletContext() || {};
  const { rawUsername, memberType } = outletCtx;

  const { partnerId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [memberMeta, setMemberMeta] = useState(null);
  const [partnerMeta, setPartnerMeta] = useState(null);
  const [form, setForm] = useState(initialFormState);
  const [initialSnapshot, setInitialSnapshot] = useState(initialFormState);

  const loadLifestyle = useCallback(async () => {
    if (!partnerId) {
      setError(new Error("Missing partnerId"));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data } = await api.get(
        `/v1/member/profile/partners/${partnerId}/lifestyle-info`,
      );

      const lifestyle = data?.lifestyle || {};

      const nextForm = {
        ...initialFormState,
        ...lifestyle,
        attraction_genders: Array.isArray(lifestyle.attraction_genders)
          ? lifestyle.attraction_genders
          : [],
        connection_types: Array.isArray(lifestyle.connection_types)
          ? lifestyle.connection_types
          : [],
        intimacy_types: Array.isArray(lifestyle.intimacy_types)
          ? lifestyle.intimacy_types
          : [],
        sexual_fantasies: Array.isArray(lifestyle.sexual_fantasies)
          ? lifestyle.sexual_fantasies
          : [],
        body_attract: Array.isArray(lifestyle.body_attract)
          ? lifestyle.body_attract
          : [],
        sexual_environment: Array.isArray(lifestyle.sexual_environment)
          ? lifestyle.sexual_environment
          : [],
        event_attract: Array.isArray(lifestyle.event_attract)
          ? lifestyle.event_attract
          : [],
        be_matched: Array.isArray(lifestyle.be_matched)
          ? lifestyle.be_matched
          : [],
      };

      setForm(nextForm);
      setInitialSnapshot(nextForm);
      setMemberMeta(data?.member || null);
      setPartnerMeta(data?.partner || null);
    } catch (err) {
      console.error(err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    loadLifestyle();
  }, [loadLifestyle]);

  const hasChanges =
    !loading && JSON.stringify(form) !== JSON.stringify(initialSnapshot);

  const setSingleField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleFromList = (field, value) => {
    setForm((prev) => {
      const list = Array.isArray(prev[field]) ? prev[field] : [];
      const exists = list.includes(value);
      return {
        ...prev,
        [field]: exists ? list.filter((v) => v !== value) : [...list, value],
      };
    });
  };

  const handleReset = () => {
    setForm(initialSnapshot);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasChanges || !partnerId) return;

    try {
      setSaving(true);
      setError(null);

      const payload = { ...form };

      const { data } = await api.patch(
        `/v1/member/profile/partners/${partnerId}/lifestyle-info`,
        payload,
      );

      const lifestyle = data?.lifestyle || payload;

      const nextForm = {
        ...initialFormState,
        ...lifestyle,
        attraction_genders: Array.isArray(lifestyle.attraction_genders)
          ? lifestyle.attraction_genders
          : [],
        connection_types: Array.isArray(lifestyle.connection_types)
          ? lifestyle.connection_types
          : [],
        intimacy_types: Array.isArray(lifestyle.intimacy_types)
          ? lifestyle.intimacy_types
          : [],
        sexual_fantasies: Array.isArray(lifestyle.sexual_fantasies)
          ? lifestyle.sexual_fantasies
          : [],
        body_attract: Array.isArray(lifestyle.body_attract)
          ? lifestyle.body_attract
          : [],
        sexual_environment: Array.isArray(lifestyle.sexual_environment)
          ? lifestyle.sexual_environment
          : [],
        event_attract: Array.isArray(lifestyle.event_attract)
          ? lifestyle.event_attract
          : [],
        be_matched: Array.isArray(lifestyle.be_matched)
          ? lifestyle.be_matched
          : [],
      };

      setForm(nextForm);
      setInitialSnapshot(nextForm);
      setMemberMeta(data?.member || memberMeta);
      setPartnerMeta(data?.partner || partnerMeta);

      toast.success(
        i18n.t("memberProfileLifestyle.status.updated", "Lifestyle updated"),
      );
    } catch (err) {
      console.error(err);
      setError(err);
      toast.error(
        i18n.t(
          "memberProfileLifestyle.status.errorSaving",
          "We couldn't save your lifestyle right now.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleBackToPartner = () => {
    if (!partnerId) return;
    navigate(`/member/my-profile/partners/${partnerId}`);
  };

  const statusLabel = memberMeta?.status || "unknown";

  const enumLabel = (groupKey, optionKey) => {
    const path = `enums.${groupKey}.${optionKey}`;
    return i18n.t(path, fallbackLabelFromKey(optionKey));
  };

  return (
    <div className={cls.page}>
      <Motion.header
        className={cls.header}
        variants={pageStagger}
        initial="hidden"
        animate="show"
      >
        {/* Back to partner single page */}
        <Motion.button
          type="button"
          className={cls.backBtn}
          onClick={handleBackToPartner}
          variants={fadeIn}
        >
          <span className={cls.backIcon} aria-hidden="true">
            ←
          </span>
          <span>
            {i18n.t(
              "partnerLifestyleInfo.backToPartner",
              "Back to partner profile",
            )}
          </span>
        </Motion.button>

        <Motion.h1 className={cls.title} variants={fadeIn}>
          {i18n.t(
            "partnerLifestyleInfo.title",
            "Partner Lifestyle & Vibe Settings",
          )}
        </Motion.h1>

        <Motion.p className={cls.subtitle} variants={fadeIn}>
          {i18n.t(
            "partnerLifestyleInfo.subtitle",
            "Fine-tune how Noty Universe understands this partner’s energy, boundaries, and the kind of connections they’re here for.",
          )}
        </Motion.p>

        <Motion.div className={cls.metaRow} variants={fadeIn}>
          {rawUsername && (
            <span className={cls.metaTag}>
              💫 {i18n.t("memberProfileLifestyle.meta.for", "For")}:{" "}
              <strong>{rawUsername}</strong>
            </span>
          )}

          {memberType && (
            <span className={cls.metaTag}>
              👥 {i18n.t("memberProfileLifestyle.meta.memberType", "Profile")}{" "}
              <strong>
                {memberType === "couple"
                  ? i18n.t(
                      "memberProfileLifestyle.meta.couple",
                      "Couple Universe",
                    )
                  : i18n.t(
                      "memberProfileLifestyle.meta.single",
                      "Single Universe",
                    )}
              </strong>
            </span>
          )}

          {memberMeta && (
            <span className={`${cls.metaTag} ${cls["metaTag--status"]}`}>
              {i18n.t("memberProfileLifestyle.meta.status", "Status")}:{" "}
              <span className={cls.statusBadge}>{statusLabel}</span>
            </span>
          )}
        </Motion.div>

        {loading && (
          <Motion.p className={cls.helperText} variants={fadeIn}>
            {i18n.t(
              "memberProfileLifestyle.status.loading",
              "Loading your lifestyle settings…",
            )}
          </Motion.p>
        )}

        {error && !loading && (
          <Motion.p className={cls.errorText} variants={fadeIn}>
            ⚠{" "}
            {i18n.t(
              "memberProfileLifestyle.status.error",
              "We couldn't load your lifestyle details. You can refresh or try again later.",
            )}
          </Motion.p>
        )}
      </Motion.header>

      {!loading && !error && (
        <Motion.form
          className={cls.form}
          onSubmit={handleSubmit}
          variants={pageStagger}
          initial="hidden"
          animate="show"
        >
          {/* Identity & Orientation */}
          <Motion.section className={cls.card} variants={fadeIn}>
            <div className={cls.cardHeader}>
              <h2 className={cls.cardTitle}>
                {i18n.t(
                  "memberProfileLifestyle.sections.identity.title",
                  "Identity & Body Vibe",
                )}
              </h2>
              <p className={cls.cardSubtitle}>
                {i18n.t(
                  "memberProfileLifestyle.sections.identity.subtitle",
                  "Tell the Universe how this partner sees themselves and the body energy they’re bringing into the room.",
                )}
              </p>
            </div>

            <div className={cls.fieldGrid}>
              {/* Sexual identity (radio) */}
              <div className={cls.fieldBlock}>
                <label className={cls.fieldLabel}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.sexualIdentity",
                    "Sexual identity",
                  )}
                </label>
                <p className={cls.fieldHelp}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.sexualIdentityHelp",
                    "Choose the label that feels the most aligned. You can always update this later.",
                  )}
                </p>

                <div className={cls.optionList}>
                  {SEXUAL_IDENTITIES.map((key) => (
                    <label key={key} className={cls.optionItem}>
                      <input
                        type="radio"
                        name="sexual_identity"
                        value={key}
                        className={cls.controlRadio}
                        checked={form.sexual_identity === key}
                        onChange={() => setSingleField("sexual_identity", key)}
                      />
                      <span className={cls.optionLabel}>
                        {enumLabel("sexualIdentities", key)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sexual role (radio) */}
              <div className={cls.fieldBlock}>
                <label className={cls.fieldLabel}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.sexualRole",
                    "Sexual role / energy",
                  )}
                </label>
                <p className={cls.fieldHelp}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.sexualRoleHelp",
                    "How do they usually move in a connection — more leading, more receiving, or a switch in between?",
                  )}
                </p>

                <div className={cls.optionList}>
                  {SEXUAL_ROLES.map((key) => (
                    <label key={key} className={cls.optionItem}>
                      <input
                        type="radio"
                        name="sexual_role"
                        value={key}
                        className={cls.controlRadio}
                        checked={form.sexual_role === key}
                        onChange={() => setSingleField("sexual_role", key)}
                      />
                      <span className={cls.optionLabel}>
                        {enumLabel("sexualRoles", key)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Attraction genders (checkbox) */}
              <div className={cls.fieldBlock}>
                <label className={cls.fieldLabel}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.attractionGenders",
                    "Which genders attract them?",
                  )}
                </label>
                <p className={cls.fieldHelp}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.attractionGendersHelp",
                    "Select all genders they’re usually drawn to when it comes to chemistry and interest.",
                  )}
                </p>

                <div className={cls.optionList}>
                  {ATTRACTION_GENDERS.map((key) => (
                    <label key={key} className={cls.optionItem}>
                      <input
                        type="checkbox"
                        name="attraction_genders"
                        value={key}
                        className={cls.controlCheckbox}
                        checked={form.attraction_genders.includes(key)}
                        onChange={() =>
                          toggleFromList("attraction_genders", key)
                        }
                      />
                      <span className={cls.optionLabel}>
                        {enumLabel("attractionGenders", key)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Motion.section>

          {/* Body & Attraction */}
          <Motion.section className={cls.card} variants={fadeIn}>
            <div className={cls.cardHeader}>
              <h2 className={cls.cardTitle}>
                {i18n.t(
                  "memberProfileLifestyle.sections.body.title",
                  "Body Vibe & Attraction",
                )}
              </h2>
              <p className={cls.cardSubtitle}>
                {i18n.t(
                  "memberProfileLifestyle.sections.body.subtitle",
                  "Celebrate the body they’re in, and the body types that turn their head.",
                )}
              </p>
            </div>

            <div className={cls.fieldGrid}>
              {/* Body type (radio) */}
              <div className={cls.fieldBlock}>
                <label className={cls.fieldLabel}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.bodyType",
                    "Their body vibe",
                  )}
                </label>
                <p className={cls.fieldHelp}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.bodyTypeHelp",
                    "How would they describe their current body energy? In progress is always allowed.",
                  )}
                </p>

                <div className={cls.optionList}>
                  {BODY_TYPES.map((key) => (
                    <label key={key} className={cls.optionItem}>
                      <input
                        type="radio"
                        name="body_type"
                        value={key}
                        className={cls.controlRadio}
                        checked={form.body_type === key}
                        onChange={() => setSingleField("body_type", key)}
                      />
                      <span className={cls.optionLabel}>
                        {enumLabel("bodyTypes", key)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Body attract (checkbox) */}
              <div className={cls.fieldBlock}>
                <label className={cls.fieldLabel}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.bodyAttract",
                    "Body types they’re attracted to",
                  )}
                </label>
                <p className={cls.fieldHelp}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.bodyAttractHelp",
                    "Pick all body vibes that usually catch their eye or feel exciting.",
                  )}
                </p>

                <div className={cls.optionList}>
                  {BODY_ATTRACT.map((key) => (
                    <label key={key} className={cls.optionItem}>
                      <input
                        type="checkbox"
                        name="body_attract"
                        value={key}
                        className={cls.controlCheckbox}
                        checked={form.body_attract.includes(key)}
                        onChange={() => toggleFromList("body_attract", key)}
                      />
                      <span className={cls.optionLabel}>
                        {enumLabel("bodyAttract", key)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Motion.section>

          {/* Environment & Energy */}
          <Motion.section className={cls.card} variants={fadeIn}>
            <div className={cls.cardHeader}>
              <h2 className={cls.cardTitle}>
                {i18n.t(
                  "memberProfileLifestyle.sections.environment.title",
                  "Environment & Party Energy",
                )}
              </h2>
              <p className={cls.cardSubtitle}>
                {i18n.t(
                  "memberProfileLifestyle.sections.environment.subtitle",
                  "Set the scenes, moods, and pace that feel like their ideal night.",
                )}
              </p>
            </div>

            <div className={cls.fieldGrid}>
              {/* Sexual environment (checkbox) */}
              <div className={cls.fieldBlock}>
                <label className={cls.fieldLabel}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.sexualEnvironment",
                    "Favourite environments",
                  )}
                </label>
                <p className={cls.fieldHelp}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.sexualEnvironmentHelp",
                    "Choose all the settings where they feel most naturally turned on and relaxed.",
                  )}
                </p>

                <div className={cls.optionList}>
                  {SEXUAL_ENVIRONMENTS.map((key) => (
                    <label key={key} className={cls.optionItem}>
                      <input
                        type="checkbox"
                        name="sexual_environment"
                        value={key}
                        className={cls.controlCheckbox}
                        checked={form.sexual_environment.includes(key)}
                        onChange={() =>
                          toggleFromList("sexual_environment", key)
                        }
                      />
                      <span className={cls.optionLabel}>
                        {enumLabel("sexualEnvironment", key)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Event attract (checkbox) */}
              <div className={cls.fieldBlock}>
                <label className={cls.fieldLabel}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.eventAttract",
                    "Event styles that attract them",
                  )}
                </label>
                <p className={cls.fieldHelp}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.eventAttractHelp",
                    "From small, intimate vibes to big takeovers – pick what calls them most.",
                  )}
                </p>

                <div className={cls.optionList}>
                  {EVENT_ATTRACT.map((key) => (
                    <label key={key} className={cls.optionItem}>
                      <input
                        type="checkbox"
                        name="event_attract"
                        value={key}
                        className={cls.controlCheckbox}
                        checked={form.event_attract.includes(key)}
                        onChange={() => toggleFromList("event_attract", key)}
                      />
                      <span className={cls.optionLabel}>
                        {enumLabel("eventAttract", key)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Alcohol & cannabis (radio) */}
              <div className={cls.fieldBlock}>
                <label className={cls.fieldLabel}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.alcoholCannabis",
                    "Alcohol & cannabis",
                  )}
                </label>
                <p className={cls.fieldHelp}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.alcoholCannabisHelp",
                    "This helps keep vibes safe, balanced, and on the same wavelength.",
                  )}
                </p>

                <div className={cls.optionList}>
                  {ALCOHOL_CANNABIS.map((key) => (
                    <label key={key} className={cls.optionItem}>
                      <input
                        type="radio"
                        name="alcohol_cannabis"
                        value={key}
                        className={cls.controlRadio}
                        checked={form.alcohol_cannabis === key}
                        onChange={() => setSingleField("alcohol_cannabis", key)}
                      />
                      <span className={cls.optionLabel}>
                        {enumLabel("alcoholCannabis", key)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Join / host frequency (radio) */}
              <div className={cls.fieldBlock}>
                <label className={cls.fieldLabel}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.joinHost",
                    "How often they join or host",
                  )}
                </label>
                <p className={cls.fieldHelp}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.joinHostHelp",
                    "Are they every-weekend energy or special-occasion only?",
                  )}
                </p>

                <div className={cls.optionList}>
                  {JOIN_HOST.map((key) => (
                    <label key={key} className={cls.optionItem}>
                      <input
                        type="radio"
                        name="join_host"
                        value={key}
                        className={cls.controlRadio}
                        checked={form.join_host === key}
                        onChange={() => setSingleField("join_host", key)}
                      />
                      <span className={cls.optionLabel}>
                        {enumLabel("joinHost", key)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Motion.section>

          {/* Connections, intimacy, fantasies, matching */}
          <Motion.section className={cls.card} variants={fadeIn}>
            <div className={cls.cardHeader}>
              <h2 className={cls.cardTitle}>
                {i18n.t(
                  "memberProfileLifestyle.sections.connections.title",
                  "Connections, Intimacy & Matching",
                )}
              </h2>
              <p className={cls.cardSubtitle}>
                {i18n.t(
                  "memberProfileLifestyle.sections.connections.subtitle",
                  "Shape the type of connections, intimacy, and adventures the Universe brings their way.",
                )}
              </p>
            </div>

            <div className={cls.fieldGrid}>
              {/* Connection types (checkbox) */}
              <div className={cls.fieldBlock}>
                <label className={cls.fieldLabel}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.connectionTypes",
                    "What are they open to?",
                  )}
                </label>
                <p className={cls.fieldHelp}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.connectionTypesHelp",
                    "Tick all connection energies that feel aligned for them right now.",
                  )}
                </p>

                <div className={cls.optionList}>
                  {CONNECTION_TYPES.map((key) => (
                    <label key={key} className={cls.optionItem}>
                      <input
                        type="checkbox"
                        name="connection_types"
                        value={key}
                        className={cls.controlCheckbox}
                        checked={form.connection_types.includes(key)}
                        onChange={() => toggleFromList("connection_types", key)}
                      />
                      <span className={cls.optionLabel}>
                        {enumLabel("connectionTypes", key)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Intimacy types (checkbox) */}
              <div className={cls.fieldBlock}>
                <label className={cls.fieldLabel}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.intimacyTypes",
                    "Preferred intimacy styles",
                  )}
                </label>
                <p className={cls.fieldHelp}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.intimacyTypesHelp",
                    "Choose how deep, soft, or playful they like intimacy to be.",
                  )}
                </p>

                <div className={cls.optionList}>
                  {INTIMACY_TYPES.map((key) => (
                    <label key={key} className={cls.optionItem}>
                      <input
                        type="checkbox"
                        name="intimacy_types"
                        value={key}
                        className={cls.controlCheckbox}
                        checked={form.intimacy_types.includes(key)}
                        onChange={() => toggleFromList("intimacy_types", key)}
                      />
                      <span className={cls.optionLabel}>
                        {enumLabel("intimacyTypes", key)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sexual fantasies (checkbox) */}
              <div className={cls.fieldBlock}>
                <label className={cls.fieldLabel}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.sexualFantasies",
                    "Fantasy themes they like",
                  )}
                </label>
                <p className={cls.fieldHelp}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.sexualFantasiesHelp",
                    "No details needed — just the themes that feel exciting or curious.",
                  )}
                </p>

                <div className={cls.optionList}>
                  {SEXUAL_FANTASIES.map((key) => (
                    <label key={key} className={cls.optionItem}>
                      <input
                        type="checkbox"
                        name="sexual_fantasies"
                        value={key}
                        className={cls.controlCheckbox}
                        checked={form.sexual_fantasies.includes(key)}
                        onChange={() => toggleFromList("sexual_fantasies", key)}
                      />
                      <span className={cls.optionLabel}>
                        {enumLabel("sexualFantasies", key)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Be matched (checkbox) */}
              <div className={cls.fieldBlock}>
                <label className={cls.fieldLabel}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.beMatched",
                    "Would they like to be matched for…",
                  )}
                </label>
                <p className={cls.fieldHelp}>
                  {i18n.t(
                    "memberProfileLifestyle.fields.beMatchedHelp",
                    "Select all kinds of trips, events, or experiences they’d love to be matched into.",
                  )}
                </p>

                <div className={cls.optionList}>
                  {BE_MATCHED.map((key) => (
                    <label key={key} className={cls.optionItem}>
                      <input
                        type="checkbox"
                        name="be_matched"
                        value={key}
                        className={cls.controlCheckbox}
                        checked={form.be_matched.includes(key)}
                        onChange={() => toggleFromList("be_matched", key)}
                      />
                      <span className={cls.optionLabel}>
                        {enumLabel("beMatched", key)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Motion.section>

          {/* Footer actions */}
          <Motion.footer className={cls.footer} variants={fadeIn}>
            <div className={cls.footerLeft}>
              {hasChanges ? (
                <span className={cls.unsaved}>
                  ✨{" "}
                  {i18n.t(
                    "memberProfileLifestyle.footer.unsaved",
                    "You have unsaved lifestyle changes.",
                  )}
                </span>
              ) : (
                <span className={cls.saved}>
                  ✅{" "}
                  {i18n.t(
                    "memberProfileLifestyle.footer.saved",
                    "Everything is synced with the Universe.",
                  )}
                </span>
              )}
            </div>

            <div className={cls.footerActions}>
              <button
                type="button"
                className={cls.secondaryBtn}
                onClick={handleReset}
                disabled={!hasChanges || saving}
              >
                {i18n.t(
                  "memberProfileLifestyle.actions.reset",
                  "Reset changes",
                )}
              </button>

              <button
                type="submit"
                className={cls.primaryBtn}
                disabled={!hasChanges || saving}
              >
                {saving
                  ? i18n.t("memberProfileLifestyle.actions.saving", "Saving…")
                  : i18n.t(
                      "memberProfileLifestyle.actions.save",
                      "Save lifestyle",
                    )}
              </button>
            </div>
          </Motion.footer>
        </Motion.form>
      )}
    </div>
  );
};

export default PartnerLifestyleInfo;
